import { useMemo } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import PageHeader from '../../../../shared/PageHeader'
import ReportFilters from '../../../../shared/ReportFilters'
import ReportJobsList from '../../../../shared/ReportJobsList'
import useAsyncReport from '../../../../hooks/useAsyncReport'
import CheckPermission from '../../../../hooks/CheckPermission'
import {
  addRefundReportJob,
  updateRefundReportJob,
  removeRefundReportJob
} from '../../../../redux/slices/AppSlice'
import { validateOneMonthRange, getOneMonthMaxDate, getMinDate } from '../../../../utils/dateRangeValidation'

const RefundReportAsync = () => {
  const { hasPermission } = CheckPermission()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const jobs = useSelector(state => state.app.refundReportJobs || [])

  const { filters, setFilters, resetFilters } = usePersistedFilters(
    'refund_report_async',
    {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date: moment().toDate(),
      group_by: 'date',
      format: 'excel'
    }
  )

  const { loading, downloadingJobs, startJob, downloadReport, removeJob, retryJob } = useAsyncReport({
    addJobAction: addRefundReportJob,
    updateJobAction: updateRefundReportJob,
    removeJobAction: removeRefundReportJob,
    createEndpoint: '/v3/reports/refund-report/jobs',
    statusEndpoint: '/v3/reports/refund-report/jobs',
    downloadEndpoint: '/v3/reports/refund-report/jobs',
    fileNamePrefix: 'refund_report',
    existingJobs: jobs,
    buildPayload: (filters, format) => ({
      start_date: moment(filters.start_date).format('YYYY-MM-DD'),
      end_date: moment(filters.end_date).format('YYYY-MM-DD'),
      group_by: filters.group_by || 'date',
      format
    })
  })

  const canGenerate = hasPermission('billing', 'get') || hasPermission('emr', 'get') || hasPermission('reports', 'get')

  const handleGenerate = async () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    await startJob(filters, filters.format)
  }

  const handleReset = () => {
    resetFilters()
  }

  const handleChange = e => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const filterConfig = useMemo(() => [
    {
      type: 'date',
      name: 'start_date',
      label: 'Start Date',
      width: 'w-[160px]'
    },
    {
      type: 'date',
      name: 'end_date',
      label: 'End Date',
      width: 'w-[160px]',
      minDate: getMinDate(filters.start_date),
      maxDate: getOneMonthMaxDate(filters.start_date)
    },
    {
      type: 'dropdown',
      name: 'group_by',
      label: 'Group By',
      options: [
        { label: 'Date Wise', value: 'date' },
        { label: 'Doctor Wise', value: 'doctor' }
      ],
      width: 'w-[160px]'
    },
    {
      type: 'dropdown',
      name: 'format',
      label: 'Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF', value: 'pdf' }
      ],
      width: 'w-[160px]'
    }
  ], [filters.start_date])

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between'>
        <PageHeader
          title={t('Refund Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={filters}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleGenerate}
        onReset={handleReset}
        loading={loading}
      />

      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t('Refund Report')}
      />
    </div>
  )
}

export default RefundReportAsync